import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  createEmailTemplate,
  emailTemplatesListQuery,
  emailTemplatesShellQuery,
  removeEmailTemplate,
  updateEmailTemplate,
  type EmailTemplateRow,
  type TemplateOption,
} from '../../api/settingsEaseedeskEmailTemplates'
import { exact, stamp } from '../../inventoree/format'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { LoadFailed } from '../../ui/LoadFailed'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Email templates.
 *
 * Element cu element fata de `version-3/easeedesk_settings/notifications/
 * templates/index.php` + `list.php`, si gest cu gest fata de `index-1.js` si
 * `list-1.js`: cautarea care ascunde randurile, previzualizarea cuprinsului,
 * data trecuta prin `moment`, si cele doua ferestre (creare si editare) cu
 * lista de simboluri alaturi.
 *
 * CUPRINSUL SE SCRIE INTR-O CASUTA, NU IN CKEDITOR
 * ------------------------------------------------
 *
 * In portal, cuprinsul se scria in CKEditor, iar simbolurile din dreapta se
 * puneau cu `editor.insertText()`. CKEditor nu vine in v4 - e o biblioteca
 * jQuery care isi scrie singura DOM-ul si se bate cap in cap cu React. Pana
 * cand editorul bogat isi capata perechea in v4, cuprinsul se scrie ca text,
 * iar simbolul se pune tot unde e cursorul. Ce se salveaza e acelasi HTML, si
 * previzualizarea il arata la fel ca in portal.
 */

/** `moment(_date).format("MMM Do YYYY, HH:mm")` din `list-1.js`. */
export function updatedLabel(value: string): string {
  const when = stamp(value)
  if (!when) return value
  return exact(when).replace(/:\d{2}$/, '')
}

/** `searchTemplate()` din `index-1.js`: filtreaza pe textul intregului rand. */
export function matchesSearch(template: EmailTemplateRow, search: string): boolean {
  if (search.trim() === '') return true
  const haystack = `${template.template_name} ${template.template_subject} ${template.template_body}`
  return haystack.toUpperCase().includes(search.toUpperCase())
}

/** Capetele ecranului asta, date ferestrei comune. */
function saveEmailTemplate(template: EmailTemplateRow | null, name: string, subject: string, body: string) {
  return template === null
    ? createEmailTemplate(name, subject, body)
    : updateEmailTemplate(template.template_id, name, subject, body)
}

export function EmailTemplatesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(emailTemplatesShellQuery())
  const list = useQuery(emailTemplatesListQuery())
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<EmailTemplateRow | null>(null)
  const [preview, setPreview] = useState<EmailTemplateRow | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'email_templates'] })
  }

  const remove = useMutation({
    mutationFn: (template: number) => removeEmailTemplate(template),
    onSuccess: () => {
      toast('Template removed.', 'Success')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The email templates" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const templates = list.data?.templates ?? []
  const shown = templates.filter((template) => matchesSearch(template, search))

  return (
    <div>
      <h4>
        <strong>Email</strong> templates
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit ticket <strong>types</strong>
      </label>
      <br />
      <button
        type="button"
        style={{ marginTop: 10 }}
        className="btn btn-success pull-right"
        onClick={() => setCreating(true)}
      >
        <i className="fas fa-plus" aria-hidden="true"></i> Create new template
      </button>
      <div style={{ marginTop: 10 }}>
        <i
          className="fa fa-search"
          aria-hidden="true"
          style={{ fontSize: 10, position: 'absolute', marginTop: 12, marginLeft: 10 }}
        ></i>
        <input
          type="text"
          id="myInput"
          placeholder="Search for template.."
          title="Types"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '15%',
            fontSize: 15,
            padding: '5px 10px 5px 40px',
            border: '1px solid #ddd',
            marginBottom: 12,
          }}
        />
      </div>
      <hr />

      <div className="list-group" id="list_group">
        {list.error ? (
          <LoadFailed what="The templates" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : templates.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No email templates.
          </div>
        ) : (
          shown.map((template) => (
            <section className="settings" key={template.template_id}>
              <div className="row">
                <div className="col-md-2">
                  <h5>
                    <i className="far fa-envelope-open" aria-hidden="true"></i> {template.template_name}
                  </h5>
                  <p className="color-green3" style={{ marginTop: 5 }}>
                    <i className="fas fa-circle" aria-hidden="true"></i> Active
                  </p>
                </div>
                <div className="col-md-2">
                  <p>{template.template_subject}</p>
                  <p className="color-blue" style={{ marginTop: 5 }}>
                    Subject
                  </p>
                </div>
                <div className="col-md-4" style={{ wordWrap: 'break-word' }}>
                  <div>{bodyExcerpt(template.template_body)}</div>
                  <a
                    href="#"
                    className="preview-template color-blue"
                    onClick={(e) => {
                      e.preventDefault()
                      setPreview(template)
                    }}
                  >
                    <i
                      className="fas fa-search-plus"
                      aria-hidden="true"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="Preview template"
                    ></i>{' '}
                    Preview email body
                  </a>
                </div>
                <div className="col-md-2">
                  <span className="updated">{updatedLabel(template.updated)}</span>
                  <p className="color-green3">
                    <i className="far fa-clock" aria-hidden="true"></i> Updated
                  </p>
                </div>
                <div className="col-md-2 aright">
                  <button type="button" className="btn btn-xs btn-primary" onClick={() => setEdited(template)}>
                    Edit
                  </button>{' '}
                  <button
                    type="button"
                    className="btn btn-xs btn-danger remove-template"
                    onClick={() => {
                      if (!window.confirm('Are you sure?')) return
                      remove.mutate(template.template_id)
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <hr className="dotted" />
            </section>
          ))
        )}
      </div>

      <Modal
        id="previewModal"
        labelledBy="previewModalLabel"
        open={preview !== null}
        onClose={() => setPreview(null)}
        title="Email body"
      >
        <div dangerouslySetInnerHTML={{ __html: preview?.template_body ?? '' }} />
      </Modal>

      <TemplateModal
        open={creating}
        template={null}
        options={shell.data.options}
        save={saveEmailTemplate}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
      <TemplateModal
        open={edited !== null}
        template={edited}
        options={shell.data.options}
        save={saveEmailTemplate}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}

/**
 * Rezumatul cuprinsului din `list.php`: 100 de caractere fara etichete, cu trei
 * puncte dupa. Masura se ia pe HTML-ul intreg, ca in macheta - de-aia un
 * cuprins scurt cu multe etichete iese uneori taiat.
 */
export function bodyExcerpt(body: string): string {
  const text = body.replace(/<[^>]*>/g, '')
  return body.length > 100 ? `${text.slice(0, 100)}...` : text
}

/**
 * `newTemplateModal` si `editTemplateModal` din macheta: aceleasi campuri,
 * aceeasi lista de simboluri. `template === null` inseamna creare.
 *
 * Salvarea vine din afara (`save`), fiindca fereastra e aceeasi si pe
 * sabloanele de email, si pe cele de telefon - doua capete de api diferite,
 * acelasi formular.
 */
export function TemplateModal({
  open,
  template,
  options,
  save: saveTemplate,
  onClose,
  onSaved,
  initial,
  preview,
  extra,
}: {
  open: boolean
  template: EmailTemplateRow | null
  options: TemplateOption[]
  save(template: EmailTemplateRow | null, name: string, subject: string, body: string): Promise<unknown>
  onClose(): void
  onSaved(): void
  /** Ce se pune in casute la creare (sablonul scris in cod, de pornire). */
  initial?: { name: string; subject: string; body: string }
  /** Previzualizarea ca TEXT, umpluta cu valori de proba - emailul catre furnizor. */
  preview?(subject: string, body: string): { subject: string; body: string }
  /** Campuri in plus, sub cuprins (furnizorii unui sablon `third_party`). */
  extra?: ReactNode
}) {
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!open) return
    setName(template?.template_name ?? initial?.name ?? '')
    setSubject(template?.template_subject ?? initial?.subject ?? '')
    setBody(template?.template_body ?? initial?.body ?? '')
  }, [open, template, initial])

  /** `editor.insertText()`: simbolul intra acolo unde e cursorul. */
  const insert = (token: string) => {
    const area = bodyRef.current
    if (!area) {
      setBody((current) => current + token)
      return
    }
    const start = area.selectionStart
    const end = area.selectionEnd
    setBody((current) => current.slice(0, start) + token + current.slice(end))
    requestAnimationFrame(() => {
      area.focus()
      area.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const save = useMutation({
    mutationFn: () => saveTemplate(template, name.trim(), subject.trim(), body),
    onSuccess: () => {
      toast(template === null ? 'New template created.' : 'Settings saved.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={template === null ? 'newTemplateModal' : 'editTemplateModal'}
      labelledBy={template === null ? 'newTemplateModalLabel' : 'editTemplateModalLabel'}
      open={open}
      onClose={onClose}
      large
      title={template === null ? 'New template' : 'Edit template'}
    >
      <form
        id={template === null ? 'new-template-form' : 'edit-template-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '' || subject.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label className="control-label">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control input-sm"
            name="template_name"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">
            Template subject <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control input-sm"
            name="template_subject"
            value={subject}
            required
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Template body</label>
          <div className="row">
            <div className="col-md-8">
              <textarea
                ref={bodyRef}
                name="template_body"
                className="template-body form-control"
                style={{ height: 220 }}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <h5>Options list</h5>
              <hr className="dotted" />
              <ul style={{ height: 220, overflowY: 'scroll' }}>
                {options.map((option) => (
                  <li key={option.token}>
                    <a
                      href="#"
                      className="template-option"
                      onClick={(e) => {
                        e.preventDefault()
                        insert(option.token)
                      }}
                    >
                      <i className="far fa-check-square" aria-hidden="true"></i> {option.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {extra}
        {preview && <TextPreview shown={preview(subject, body)} />}
        <hr className="dotted" />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-field"
            disabled={name.trim() === '' || subject.trim() === '' || save.isPending}
          >
            {template === null ? 'Create' : 'Update'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Previzualizarea unui email TEXT: React scapa tot, iar capetele de rand se
 * pastreaza (`pre-wrap`). Nimic din sablon nu se deseneaza ca HTML.
 */
export function TextPreview({ shown }: { shown: { subject: string; body: string } }) {
  return (
    <div className="form-group tp-template-preview" aria-label="Email preview">
      <label className="control-label">Preview (sample data)</label>
      <div style={{ border: '1px solid #ddd', borderRadius: 3, background: '#fafafa', padding: '8px 10px' }}>
        <div>
          <strong>Subject:</strong> {shown.subject || '-'}
        </div>
        <hr className="dotted" style={{ margin: '6px 0' }} />
        <div className="tp-template-preview-body" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          {shown.body}
        </div>
      </div>
    </div>
  )
}
